import { useEffect, useState, useCallback } from "react";
import { Bell, X, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useNavigate } from "react-router-dom";

/**
 * Header bell icon with unread counter + dropdown of recent notifications.
 * Polls every 30s. Marks a notification read on click and navigates to relevant page.
 */
export default function NotificationBell() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/notifications?limit=15");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/my/notifications/${n.id}/read`); } catch (e) { /* silent */ }
    }
    setOpen(false);
    load();
    // Deep-link based on type
    const type = n.type || "";
    if (type.startsWith("transport.")) {
      const tripId = n.meta?.trip_id;
      if (tripId) nav(`/parent/trip/${tripId}`);
    } else if (type.startsWith("wallet.")) {
      nav("/canteen/wallet");
    } else if (type.startsWith("payment.")) {
      nav("/fees");
    } else if (type.startsWith("message.")) {
      nav("/messages");
    }
  };

  const markAll = async () => {
    try { await api.post("/my/notifications/read-all"); await load(); } catch (e) { /* silent */ }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-muted smooth"
        aria-label="notifications"
        data-testid="notif-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="notif-unread-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute end-0 mt-2 w-80 max-w-[92vw] bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden animate-fade-up"
               data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="font-semibold text-sm">{t("notifications") || "الإشعارات"}</div>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={markAll} className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                          data-testid="notif-mark-all-read">
                    <CheckCheck className="w-3.5 h-3.5" /> {t("mark_all_read") || "اقرأ الكل"}
                  </button>
                )}
                <button onClick={() => setOpen(false)} aria-label="close" className="p-0.5 hover:bg-muted rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {t("no_notifications") || "لا توجد إشعارات"}
                </div>
              ) : items.map(n => (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`w-full text-start p-3 border-b border-border/40 last:border-0 hover:bg-muted/50 smooth block ${!n.read ? "bg-primary/5" : ""}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm truncate flex-1">{n.title}</div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>
                  <div className="text-[10px] text-muted-foreground/70 mt-1 font-mono">{(n.at || "").slice(0, 16).replace("T", " ")}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
